import React, { useEffect, useRef } from "react";
import { useAuthStore } from "../../@shared/store/useAuthStore";
import SpriteAnimator from "./SpriteAnimator";
import {
  motion,
  useAnimation,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { Box, Typography } from "@mui/material";
import { useState } from "react";
import AudioManager from "../../@shared/utils/AudioManager";
import { useTutorial } from "../../@shared/utils/useTutorial";

const PlayerPlatform = ({
  id,
  name,
  character,
  isSelected,
  onSelect,
  playerTurn,
  isBot,
  activeAnimation,
  nextTurn,
  cpMovesLeft,
  playerMoveType,
  lastMove,
  isTutorial,
  coOp,
}) => {
  const { userData } = useAuthStore();
  const { progress, sequence, advanceStep } = useTutorial(userData?.id);
  const isCurrentPlayer = id === userData.id;

  const [incomingCardImg, setIncomingCardImg] = useState(null);

  const animationCooldownRef = useRef(false);
  const [animationName, setAnimationName] = useState(null); // 💡 Local state for sprite animationName
  const spriteRef = useRef();
  const lastProcessedMoveRef = useRef(null);

  const bounce = useMotionValue(0);
  const blinkControls = useAnimation();
  const controls = useAnimation();

  const shadowScale = useTransform(bounce, [0, -5], [1, 0.875]);

  const handleShake = (num = 3) => {
    bounce.set(10);
    controls.start({
      x: [0, -10, 0],
      transition: {
        duration: 0.2,
        repeat: num,
        repeatType: "reverse",
      },
    });
  };

  const handleTutorialBattleAdvance = () => {
    if (progress?.currentStepIndex === 0 && character?.name === "Luna") {
      advanceStep();
    }
  };

  useEffect(() => {
    if (isSelected) {
      controls.start({
        y: [-5, 5],
        x: [-5, 5],
        skewX: ["50deg", "50deg"],
        transition: {
          duration: 0.4,
          repeat: Infinity,
          repeatType: "reverse",
        },
      });
    } else {
      controls.start({
        y: 10,
        x: 10,
        transition: { duration: 0.3 },
        skewX: "50deg",
      });
    }
  }, [isSelected]);

  //   🔥 Watch for animation triggers
  useEffect(() => {
    if (!lastMove) return;

    const { type, target, defender, timestamp, card } = lastMove;

    // ✅ Skip if we've already handled this move
    if (lastProcessedMoveRef.current === timestamp) return;
    lastProcessedMoveRef.current = timestamp;

    animationCooldownRef.current = timestamp;

    if (type === "attack" && (target === name || defender === name)) {
      setTimeout(() => {
        blinkControls.start({
          opacity: [1, 0, 1],
          transition: {
            duration: 0.2,
            repeat: 10,
            ease: "easeInOut",
          },
        });
      }, 2000);
      console.log("🔥 ATTACK ANIMATION TRIGGERED");
    } else if (type === "playCard" && target === name && card?.imageUrl) {
      setIncomingCardImg(card.imageUrl);

      setTimeout(() => {
        AudioManager.playSFX("buff1");
      }, 1500);

      setTimeout(() => {
        setIncomingCardImg(null);
      }, 4500);

      console.log("🎴 CARD PLAY ANIMATION TRIGGERED");
    }

    setTimeout(() => {
      animationCooldownRef.current = false;
    }, 3000);
  }, [lastMove, name]);

  const containerBackground = isSelected
    ? "#ef169c"
    : playerTurn
    ? "#fbcf57"
    : coOp
    ? isBot
      ? "#0070f7"
      : "#4ad24a"
    : isCurrentPlayer
    ? "#30d8fc"
    : "#0070f7";

  const borderColor = isSelected
    ? "#f96ed0"
    : playerTurn
    ? "#956f07"
    : coOp
    ? isBot
      ? "#0070f7"
      : isCurrentPlayer
      ? "white"
      : "#003100"
    : isCurrentPlayer
    ? "#16116e"
    : "#30d8fc";

  return (
    <motion.div
      animate={blinkControls}
      initial={{ opacity: 1 }}
      style={{
        ...styles.playerContainer,
        backgroundColor: containerBackground,
        borderColor,
        boxShadow: isSelected
          ? "10px 7px 1px #b90c98"
          : playerTurn
          ? "10px 7px 1px #af8001"
          : coOp
          ? isBot
            ? "10px 7px 1px #16116e"
            : "10px 7px 1px #003100"
          : isCurrentPlayer
          ? "10px 7px 1px rgb(0, 150, 184)"
          : "10px 7px 1px #16116e",
      }}
    >
      <Box
        onClick={() => {
          if (playerMoveType === "attack" && playerTurn && id === userData.id) {
            //shake the character
            handleShake();
            return;
          } else {
            onSelect();
            if (isTutorial) {
              handleTutorialBattleAdvance();
            }
          }
        }}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
        }}
      >
        {!character.isDead ? (
          <>
            {/* {playerTurn && (
              <Box
                sx={{
                  transform: "skewX(50deg)",
                  marginLeft: "50%",
                  marginTop: "-15%",
                  zIndex: 5000,
                }}
              >
                <Typography style={styles.turnIndicatorText}>
                  {cpMovesLeft} / {"2"}
                </Typography>
              </Box>
            )} */}
            {incomingCardImg && (
              <motion.img
                src={incomingCardImg}
                alt="played card"
                initial={{ y: -100, x: -100, opacity: 0, skewX: "50deg" }}
                animate={{ y: 0, x: 0, opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: 2 }}
                style={{
                  position: "absolute",
                  width: "30px",
                  height: "auto",
                  zIndex: 150,
                  top: "0%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  borderRadius: 6,
                  pointerEvents: "none",
                }}
              />
            )}

            <motion.img
              src={character.imageUrl}
              alt="character"
              animate={controls}
              style={{
                ...styles.playerImage,
                transform: "skewX(50deg)",
                left: window.innerWidth >= 900 ? "-6%" : "-75%",
                width: window.innerWidth >= 900 ? "70px" : "50px",
                height: window.innerWidth >= 900 ? "70px" : "50px",
              }}
            />

            {/* 🌀 Dynamic animation layer */}
            {animationName && (
              <SpriteAnimator
                ref={spriteRef}
                animationName={animationName}
                frameWidth={64}
                frameHeight={64}
                frameCount={12}
                fps={activeAnimation?.fps || 8}
                loop={true}
                style={[
                  styles.playerImage,
                  {
                    height: 64,
                    width: 64,
                    top: -24,
                    left: -5,
                    zIndex: 101,
                  },
                ]}
              />
            )}

            {character.stance === "defense" && (
              <img
                src={require("../../@shared/assets/icons/sheild.png")}
                alt="defense"
                style={{
                  position: "absolute",

                  top: window.innerWidth >= 900 ? -10 : -2,
                  left: window.innerWidth >= 900 ? "15%" : "-30%",
                  height: window.innerWidth >= 900 ? 40 : 25,
                  width: window.innerWidth >= 900 ? 40 : 25,

                  transform: "skewX(50deg)",
                  zIndex: 100,
                }}
              />
            )}
            <motion.div
              style={{
                ...styles.shadowCircle,
                transform: "skewX(50deg) translateX(-34px) translateY(38px)",
                scale: shadowScale,
              }}
            />
          </>
        ) : (
          <SpriteAnimator
            ref={spriteRef}
            animationName="deadAshPile"
            frameWidth={64}
            frameHeight={64}
            frameCount={12}
            fps={12}
            loop={true}
            onFinish={() => console.log("animation done")}
            style={{
              height: 64,
              width: 64,
              top: window.innerWidth >= 900 ? "-100%" : "-100%",
              left: window.innerWidth >= 900 ? "10%" : "-70%",
              position: "absolute",
              transform: "skewX(50deg)",
            }}
          />
        )}
      </Box>
    </motion.div>
  );
};

const styles = {
  playerContainer: {
    width: window.innerWidth >= 900 ? "30%" : "25%",
    left: window.innerWidth >= 900 ? "-10%" : "5%",
    top: window.innerWidth >= 900 ? -15 : -15,
    height: 36,
    position: "relative",
    margin: "2px",
    border: "1px solid #30d8fc",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // overflow: "hidden",
  },
  playerImage: {
    width: "70px",
    height: "70px",
    borderRadius: "25px",
    position: "absolute",
    bottom: "60%",
    left: "-6%",
    zIndex: 100,
    transform: "skewX(50deg)",
  },
  shadowCircle: {
    width: "50%",
    height: "20%",
    borderRadius: "50%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    position: "absolute",
    bottom: "25%",
    left: "25%",
    zIndex: 10,
  },
  turnIndicatorText: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#fff",
    transform: [{ skewX: "50deg" }],
    position: "absolute",
    top: -40,
    left: "-60%",
    padding: "2px 6px",
    backgroundColor: "#16116e",
    borderRadius: 4,
    zIndex: 100,
  },
};

export default PlayerPlatform;
